import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ICallFlowStep } from '@dataTypes/clinic.type'
import StepPanelContainer from '../StepPanelContainer.component'
import './PlayAudioPanelStyle.css'
import { ActionButton, Button, AudioPlayer } from '@enamelai/design-system'
import { Icon as CloseIcon } from '@enamelai/design-system/icons/Close'

export type PlayAudioPanelProps = {
  step: Extract<ICallFlowStep, { type: 'play_audio' }>
  branchLabel: string
  onChange: (
    patch: Partial<Extract<ICallFlowStep, { type: 'play_audio' }>>,
  ) => void
  onCancel: () => void
  onDone: () => void
  onPendingFile?: (file: File | null) => void
}

const AUDIO_ACCEPT = '.mp3,.wav,audio/mpeg,audio/wav,audio/*'
const MAX_BYTES = 50 * 1024 * 1024
const INVALID_AUDIO_MSG = 'Invalid file. Please upload a valid audio file'
const INVALID_SIZE_MSG = 'File is too large. Maximum size is 50 MB.'
const REQUIRED_AUDIO_MSG = 'Audio is required on Play Audio configuration'

const AUDIO_EXT_RE = /\.(mp3|wav|m4a|aac|ogg|flac|webm|opus)$/i

const isAudioMimeOrExtension = (file: File): boolean => {
  if (file.type && file.type.startsWith('audio/')) return true
  return AUDIO_EXT_RE.test(file.name)
}

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type LocalPreview = { url: string; file: File }

const PlayAudioPanel = ({
  step,
  branchLabel: _branchLabel,
  onChange,
  onCancel,
  onDone,
  onPendingFile,
}: PlayAudioPanelProps): React.ReactElement => {
  void _branchLabel
  const fileRef = useRef<HTMLInputElement>(null)
  const previewRef = useRef<LocalPreview | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const [saveValidation, setSaveValidation] = useState(false)
  const [localPreview, setLocalPreview] = useState<LocalPreview | null>(null)

  const hasFile = Boolean(step.fileName?.trim() || step.audioUrl?.trim())

  const bytesForValidation = step.fileSizeBytes ?? localPreview?.file.size ?? 0
  const audioSaveValid = Boolean(
    step.fileName?.trim() && bytesForValidation > 0,
  )

  useEffect(() => {
    if (audioSaveValid) setSaveValidation(false)
  }, [audioSaveValid])

  const replacePreview = (next: LocalPreview | null): void => {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current.url)
    }
    previewRef.current = next
    setLocalPreview(next)
  }

  useEffect(() => {
    if (!step.fileName?.trim()) {
      replacePreview(null)
    }
  }, [step.fileName])

  useEffect(() => {
    return () => {
      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current.url)
        previewRef.current = null
      }
    }
  }, [])

  const applyFile = useCallback(
    (file: File | undefined) => {
      if (!file) return
      if (!isAudioMimeOrExtension(file)) {
        setFileError(INVALID_AUDIO_MSG)
        return
      }
      if (file.size > MAX_BYTES) {
        setFileError(INVALID_SIZE_MSG)
        return
      }
      setFileError(null)
      setSaveValidation(false)
      onPendingFile?.(file)
      replacePreview({ url: URL.createObjectURL(file), file })
      onChange({ fileName: file.name, fileSizeBytes: file.size })
    },
    [onChange, onPendingFile],
  )

  const openPicker = useCallback(() => {
    fileRef.current?.click()
  }, [])

  const clearFile = useCallback(() => {
    setFileError(null)
    setSaveValidation(false)
    onPendingFile?.(null)
    replacePreview(null)
    onChange({
      fileName: undefined,
      audioUrl: undefined,
      fileSizeBytes: undefined,
    })
    if (fileRef.current) {
      fileRef.current.value = ''
    }
  }, [onChange, onPendingFile])

  const playbackSrc =
    localPreview?.url ?? (step.audioUrl?.trim() ? step.audioUrl : '')

  const displaySizeBytes = step.fileSizeBytes ?? localPreview?.file.size ?? 0

  const handleDone = useCallback(() => {
    if (!audioSaveValid) {
      setSaveValidation(true)
      return
    }
    setSaveValidation(false)
    onDone()
  }, [audioSaveValid, onDone])

  return (
    <StepPanelContainer
      title="Play audio"
      confirmLabel="Save"
      onCancel={onCancel}
      onDone={handleDone}
    >
      <div className="scp-panel-field scp-play-audio-field">
        <span className="scp-panel-label">Upload audio file</span>
        {fileError && <span className="hint-text">{fileError}</span>}
        {saveValidation && !audioSaveValid && (
          <span className="hint-text">{REQUIRED_AUDIO_MSG}</span>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={AUDIO_ACCEPT}
          className="scp-step-file-input-hidden"
          aria-hidden
          tabIndex={-1}
          onChange={(e) => {
            applyFile(e.target.files?.[0])
            e.target.value = ''
          }}
        />

        {!hasFile ? (
          <div
            className={`scp-play-audio-upload-box${dragOver ? ' scp-play-audio-drop-zone' : ''}${saveValidation ? ' scp-destructive-box' : ''}`}
            role="button"
            tabIndex={0}
            aria-label="Upload audio file"
            onClick={openPicker}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return
              e.preventDefault()
              openPicker()
            }}
            onDragEnter={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragOver(true)
            }}
            onDragOver={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragOver(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setDragOver(false)
              }
            }}
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragOver(false)
              applyFile(e.dataTransfer.files?.[0])
            }}
          >
            <div className="scp-play-audio-upload-main">
              <i className="upload-icon mgc_play_circle_fill" />
              <span>
                Drop your audio file here, or{' '}
                <Button
                  type="button"
                  size="md"
                  variant="ghost"
                  className="button-link"
                  onClick={(e) => {
                    e.stopPropagation()
                    openPicker()
                  }}
                >
                  click here
                </Button>{' '}
                to upload
              </span>
              <span className="scp-play-audio-sub-title">
                MP3, WAV (50 MB max size)
              </span>
            </div>
          </div>
        ) : (
          <>
            <div className="scp-play-audio-file-card">
              <i className="mgc-icon mgc_file_music_line" />
              <div className="scp-play-audio-file-card-meta">
                <span className="scp-play-audio-file-card-name">
                  {step.fileName?.split('/').pop()}
                </span>
                <span className="scp-play-audio-file-card-size">
                  {displaySizeBytes > 0
                    ? formatFileSize(displaySizeBytes)
                    : '—'}
                </span>
              </div>
              <ActionButton
                size="sm"
                tone="danger"
                variant="outline"
                label="Remove audio"
                icon={
                  <CloseIcon
                    className="ds-action-button__svg"
                    aria-hidden="true"
                  />
                }
                onClick={clearFile}
              />
            </div>
            {playbackSrc ? <AudioPlayer src={playbackSrc} /> : null}
          </>
        )}
      </div>
    </StepPanelContainer>
  )
}

export default PlayAudioPanel
